import React, { useState } from 'react';
import { Link } from 'react-router-dom';

import { Token } from '@stripe/stripe-js';

import { useStripeTokenizeCardElement } from '@shared/hooks/use_stripe_tokenize_card_element';

import { payURL } from '@portal/config/routes';
import { Billing__BuildSourceInput, Status, useBuildSourceMutation, useDelinquentBalanceQuery } from '@portal/schema';

import { StyledStripeElements } from './styled_stripe_elements';

type FormProps = Omit<Billing__BuildSourceInput, 'token'> & {
  canAddPrepaid?: boolean;
  onSave(sourceID?: string, token?: Token): void;
  children(props: {
    fields: {
      name?: string;
      onName(name: string | undefined): void;
    };
    error?: string | JSX.Element;
    saving: boolean;
    saveable: boolean;
    save(): void;
  }): React.ReactElement;
};

const prepaidErrorMessage = (delinquent: boolean, canAddPrepaid: boolean) => {
  if (delinquent && canAddPrepaid) {
    return null;
  }
  return (
    <>
      Prepaid cards can only be used to pay off a past due balance and cannot be added as a payment method for your
      account.{' '}
      {delinquent ? (
        <>
          <Link to={payURL()}>Click here to pay off your past due balance</Link> using a prepaid card.
        </>
      ) : (
        <>Please add a debit or credit card instead.</>
      )}
    </>
  );
};

const SourceFormContent: React.FC<FormProps> = ({
  canAddPrepaid = false,
  children,
  onSave,
  signedAccountID,
  signedOrderID,
  makeDefault,
}) => {
  const { data: balanceData } = useDelinquentBalanceQuery();

  const { error, tokenize, tokenizing } = useStripeTokenizeCardElement();

  const [name, setName] = useState<string | undefined>(undefined);
  const [prepaidErrors, setPrepaidErrors] = useState<JSX.Element | null>();
  const [save, { loading, data }] = useBuildSourceMutation();

  const saveable = !!name;

  return children({
    fields: {
      name,
      onName: setName,
    },
    error: data?.buildSource?.error || prepaidErrors || error,
    saving: tokenizing || loading,
    save: async () => {
      if (tokenizing || loading) return;

      setPrepaidErrors(null);
      const token = await tokenize({ name });
      if (!token) return;

      if (token.card?.funding === 'prepaid') {
        const delinquent = !!balanceData?.account.delinquentBalance;
        const errorMessage = prepaidErrorMessage(delinquent, canAddPrepaid);
        if (errorMessage) {
          setPrepaidErrors(errorMessage);
          return;
        }
      }
      const input = {
        token: token.id,
        signedAccountID,
        signedOrderID,
        makeDefault,
      };
      const result = await save({ variables: { input } });
      if (result?.data?.buildSource?.status === Status.Ok) {
        onSave(result.data.buildSource.source?.id, token);
      }
    },
    saveable,
  });
};

export const SourceForm: React.FC<FormProps> = (props) => (
  <StyledStripeElements>
    <SourceFormContent {...props} />
  </StyledStripeElements>
);
